import { Loader2, Save } from "lucide-react";
import { InlineWarning, Panel, StatusBadge } from "@/components/common/Primitives";
import { TariffPeriodsPanel } from "@/components/bpm/TariffPeriodsPanel";
import { RiskSettingsPanel } from "@/components/risks/RiskSettingsPanel";
import { eur } from "@/lib/format";

const inputCls =
  "h-8 w-full rounded-sm border border-slate-300 bg-white px-2 text-[12.5px] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200";

const VALUATION_FIELDS = [
  ["quick_sale_discount_pct", "Korting snelle verkoop (%)"],
  ["optimistic_premium_pct", "Opslag optimistisch (%)"],
  ["mileage_adj_pct_per_10000km", "Kilometercorrectie (% per 10.000 km)"],
  ["age_adj_pct_per_month", "Leeftijdscorrectie (% per maand)"],
  ["min_similarity", "Minimale overeenkomstscore"],
  ["outlier_mad_threshold", "Outlier-drempel (MAD z-score)"],
  ["max_total_adjustment_pct", "Maximale totale correctie (%)"],
];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="label-xs">{label}</label>
    <div className="mt-1">{children}</div>
    {hint && <p className="mt-0.5 text-[10.5px] text-slate-500">{hint}</p>}
  </div>
);

const SaveButton = ({ sections, testid, onSave, busy }) => (
  <button
    type="button"
    onClick={() => onSave(sections)}
    disabled={busy}
    data-testid={testid}
    className="flex items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
  >
    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
    Opslaan
  </button>
);

export const GeneralSection = ({ settings, setSection, onSave, busy }) => (
  <Panel title="Algemeen" actions={<SaveButton sections={["general"]} testid="save-general" onSave={onSave} busy={busy} />} testid="settings-general">
    <div className="grid gap-3 p-3 md:grid-cols-3">
      <Field label="Valuta">
        <input value={settings.general.currency} readOnly className={`${inputCls} bg-slate-50`} />
      </Field>
      <Field label="Taal">
        <select value={settings.general.language} onChange={(e) => setSection("general", "language", e.target.value)} className={inputCls}>
          <option value="nl">Nederlands</option>
        </select>
      </Field>
      <Field label="Standaard bronland">
        <select
          value={settings.general.default_source_country}
          data-testid="setting-default-country"
          onChange={(e) => setSection("general", "default_source_country", e.target.value)}
          className={inputCls}
        >
          <option value="DE">Duitsland</option>
          <option value="NL">Nederland</option>
          <option value="BE">België</option>
        </select>
      </Field>
    </div>
  </Panel>
);

export const TradingSection = ({ settings, setSection, onSave, busy }) => (
  <Panel title="Handelsdrempels" actions={<SaveButton sections={["trading"]} testid="save-trading" onSave={onSave} busy={busy} />} testid="settings-trading">
    <div className="grid gap-3 p-3 md:grid-cols-3">
      <Field label="Minimale handelsbijdrage (€)" hint="Standaard € 10.000">
        <input type="number" value={settings.trading.min_contribution} data-testid="setting-min-contribution" onChange={(e) => setSection("trading", "min_contribution", Number(e.target.value))} className={inputCls} />
      </Field>
      <Field label="Minimale ROI (%)" hint="Standaard 8%">
        <input type="number" step="0.1" value={settings.trading.min_roi_pct} data-testid="setting-min-roi" onChange={(e) => setSection("trading", "min_roi_pct", Number(e.target.value))} className={inputCls} />
      </Field>
      <Field label="Minimale Deal Score" hint="Standaard 65">
        <input type="number" value={settings.trading.min_deal_score} data-testid="setting-min-score" onChange={(e) => setSection("trading", "min_deal_score", Number(e.target.value))} className={inputCls} />
      </Field>
      <Field label="Minimale waarderingsbetrouwbaarheid (%)" hint="Standaard 60%">
        <input type="number" value={settings.trading.min_confidence_pct} onChange={(e) => setSection("trading", "min_confidence_pct", Number(e.target.value))} className={inputCls} />
      </Field>
      <Field label="BTW-filter">
        <select value={settings.trading.vat_filter} onChange={(e) => setSection("trading", "vat_filter", e.target.value)} className={inputCls}>
          <option value="all">Alle fiscale statussen</option>
          <option value="BTW aftrekbaar">Alleen BTW aftrekbaar</option>
          <option value="Marge-auto">Alleen marge-auto's</option>
        </select>
      </Field>
      <Field label="Verkoper">
        <select value={settings.trading.seller_filter} onChange={(e) => setSection("trading", "seller_filter", e.target.value)} className={inputCls}>
          <option value="both">Dealer én particulier</option>
          <option value="Dealer">Dealer</option>
          <option value="Particulier">Particulier</option>
        </select>
      </Field>
    </div>
  </Panel>
);

export const CostsSection = ({ settings, setSection, labels, onSave, busy }) => (
  <Panel
    title="Kostenaannames"
    subtitle="Dit zijn configureerbare aannames, geen vaste feitelijke kosten"
    actions={<SaveButton sections={["costs"]} testid="save-costs" onSave={onSave} busy={busy} />}
    testid="settings-costs"
  >
    <div className="p-3">
      <InlineWarning tone="info">
        Elke waarde is een <b>standaard aanname</b>. Wijzigingen worden direct meegenomen in nieuwe
        berekeningen; per voertuig kun je afwijken via de detailpagina.
      </InlineWarning>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {Object.entries(settings.costs).map(([key, value]) => (
          <Field key={key} label={labels[key] || key} hint={`Huidig: ${eur(value)}`}>
            <input type="number" value={value} data-testid={`setting-cost-${key}`} onChange={(e) => setSection("costs", key, Number(e.target.value))} className={inputCls} />
          </Field>
        ))}
      </div>
    </div>
  </Panel>
);

export const ValuationSection = ({ settings, setSection, onSave, busy }) => (
  <Panel title="Waarderingsparameters" actions={<SaveButton sections={["valuation"]} testid="save-valuation" onSave={onSave} busy={busy} />} testid="settings-valuation">
    <div className="grid gap-3 p-3 md:grid-cols-3">
      {VALUATION_FIELDS.map(([key, label]) => (
        <Field key={key} label={label}>
          <input type="number" step="0.1" value={settings.valuation[key]} data-testid={`setting-valuation-${key}`} onChange={(e) => setSection("valuation", key, Number(e.target.value))} className={inputCls} />
        </Field>
      ))}
    </div>
  </Panel>
);

export const BpmSection = ({ settings, setSection, tariffYears, periods, integrity, depreciation, busy, onSave, reloadTariffs }) => (
  <div className="space-y-2">
    <Panel title="BPM-configuratie" actions={<SaveButton sections={["bpm"]} testid="save-bpm" onSave={onSave} busy={busy} />} testid="settings-bpm">
      <div className="grid gap-3 p-3 md:grid-cols-3">
        <Field label="Tariefkeuze" hint="Bij gebruikte import mag het historische tarief worden gekozen">
          <select value={settings.bpm.tariff_selection} data-testid="setting-tariff-selection" onChange={(e) => setSection("bpm", "tariff_selection", e.target.value)} className={inputCls}>
            <option value="lowest_valid">Laagste toegestane historische tarief</option>
            <option value="current">Alleen huidig tariefjaar</option>
          </select>
        </Field>
        <Field label="Huidig tariefjaar">
          <select value={settings.bpm.tariff_year} data-testid="setting-tariff-year" onChange={(e) => setSection("bpm", "tariff_year", Number(e.target.value))} className={inputCls}>
            {tariffYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Afschrijvingsmethode">
          <select value={settings.bpm.depreciation_method} onChange={(e) => setSection("bpm", "depreciation_method", e.target.value)} className={inputCls}>
            <option value="forfaitaire_tabel">Forfaitaire tabel</option>
          </select>
        </Field>
      </div>
    </Panel>

    <TariffPeriodsPanel periods={periods} integrity={integrity} busy={busy} onReload={reloadTariffs} />

    <Panel title="Afschrijvingstabel (forfaitair)" testid="settings-depreciation">
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Vanaf (maanden)</th>
              <th>Tot (maanden)</th>
              <th>Basisafschrijving</th>
              <th>Per extra maand</th>
            </tr>
          </thead>
          <tbody>
            {depreciation.map((row) => (
              <tr key={`${row.from_months}-${row.to_months ?? "max"}`}>
                <td className="num">{row.from_months}</td>
                <td className="num">{row.to_months ?? "∞"}</td>
                <td className="num">{row.base_pct}%</td>
                <td className="num">{row.pct_per_month}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  </div>
);

export const RiskSection = ({ riskCatalog, settings, setSettings, onSave, busy }) => (
  <div className="space-y-3">
    <div className="flex justify-end">
      <SaveButton sections={["risk"]} testid="save-risk" onSave={onSave} busy={busy} />
    </div>
    <RiskSettingsPanel
      catalog={riskCatalog}
      config={settings.risk || {}}
      onChange={(next) => setSettings((s) => ({ ...s, risk: next }))}
    />
  </div>
);

export const SourcesSection = ({ providers }) => (
  <Panel title="Databronnen" testid="settings-sources">
    <div className="divide-y divide-slate-100">
      {providers.map((p) => (
        <div key={p.key} className="flex items-start justify-between gap-3 px-3 py-2.5">
          <div className="min-w-0">
            <div className="text-[12.5px] font-semibold text-slate-800">{p.label}</div>
            <div className="text-[11px] text-slate-500">{p.description}</div>
            <div className="mt-0.5 text-[10.5px] text-slate-400">
              Landen: {(p.countries || []).join(", ") || "—"} · sleutel: {p.key}
            </div>
          </div>
          {p.enabled ? (
            <span className="chip border-blue-300 bg-blue-50 text-blue-800">Selecteerbaar</span>
          ) : (
            <StatusBadge status="LATER_BESCHIKBAAR" label="Later beschikbaar" />
          )}
        </div>
      ))}
    </div>
    <div className="border-t border-slate-100 px-3 py-2 text-[11px] text-slate-500">
      Credentials worden uitsluitend server-side in omgevingsvariabelen bewaard en zijn nooit
      zichtbaar in de frontend.
    </div>
  </Panel>
);

export const AiSection = ({ settings, setSection, onSave, busy }) => (
  <Panel title="AI-configuratie" actions={<SaveButton sections={["ai"]} testid="save-ai" onSave={onSave} busy={busy} />} testid="settings-ai">
    <div className="grid gap-3 p-3 md:grid-cols-3">
      <Field label="Provider">
        <select value={settings.ai.provider} onChange={(e) => setSection("ai", "provider", e.target.value)} className={inputCls}>
          <option value="openai">OpenAI</option>
          <option value="anthropic">Anthropic</option>
          <option value="gemini">Gemini</option>
        </select>
      </Field>
      <Field label="Model">
        <input value={settings.ai.model} data-testid="setting-ai-model" onChange={(e) => setSection("ai", "model", e.target.value)} className={inputCls} />
      </Field>
      <Field label="AI-analyse actief">
        <select value={settings.ai.enabled ? "1" : "0"} onChange={(e) => setSection("ai", "enabled", e.target.value === "1")} className={inputCls}>
          <option value="1">Aan</option>
          <option value="0">Uit</option>
        </select>
      </Field>
    </div>
    <div className="border-t border-slate-100 p-3">
      <InlineWarning tone="info">
        De AI-laag interpreteert uitsluitend de door de backend berekende cijfers en mag geen
        financiële waarden berekenen of ontbrekende data verzinnen.
      </InlineWarning>
    </div>
  </Panel>
);

export const UserSection = ({ user, pw, setPw, changePassword, busy }) => (
  <Panel title="Account" testid="settings-user">
    <div className="grid gap-3 p-3 md:grid-cols-2">
      <Field label="Naam">
        <input value={user?.name || ""} readOnly className={`${inputCls} bg-slate-50`} />
      </Field>
      <Field label="E-mailadres">
        <input value={user?.email || ""} readOnly className={`${inputCls} bg-slate-50`} />
      </Field>
      <Field label="Rol">
        <input value={user?.role || ""} readOnly className={`${inputCls} bg-slate-50`} />
      </Field>
    </div>
    <div className="border-t border-slate-100 p-3">
      <div className="label-xs mb-2">Wachtwoord wijzigen</div>
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="Huidig wachtwoord">
          <input type="password" value={pw.current_password} data-testid="current-password-input" onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Nieuw wachtwoord (min. 8)">
          <input type="password" value={pw.new_password} data-testid="new-password-input" onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} />
        </Field>
        <div className="flex items-end">
          <button
            type="button"
            onClick={changePassword}
            disabled={busy}
            data-testid="change-password-button"
            className="h-8 rounded-sm bg-blue-600 px-3 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Wachtwoord wijzigen
          </button>
        </div>
      </div>
    </div>
  </Panel>
);
